import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { Send, MapPin, Phone, Mail, Globe, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SITE, EMAILJS_CONFIG } from "@/lib/site-data";

export default function Contact() {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    user_name: "",
    user_email: "",
    user_phone: "",
    user_subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    if (!form.user_name.trim()) {
      toast.error("Por favor escribe tu nombre.");
      return false;
    }
    if (!form.user_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.user_email)) {
      toast.error("Ingresa un correo electrónico válido.");
      return false;
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      toast.error("Cuéntanos un poco más sobre tu proyecto (mín. 10 caracteres).");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await emailjs.sendForm(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        formRef.current,
        { publicKey: EMAILJS_CONFIG.publicKey }
      );
      toast.success("Mensaje enviado. Nuestro equipo te contactará pronto.");
      setSent(true);
      setForm({
        user_name: "",
        user_email: "",
        user_phone: "",
        user_subject: "",
        message: "",
      });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error("EmailJS error:", err);
      toast.error(
        "No se pudo enviar el mensaje. Intenta de nuevo o escríbenos directamente a " +
          SITE.email
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 blueprint-grid opacity-20 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT: Heading + Info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="block w-8 h-px bg-[#385723]" />
              <span className="overline">06 · Contacto</span>
            </div>
            <h2 className="heading-lg text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              Construyamos
              <br />
              <span className="text-[#385723]">tu próximo</span>
              <br />
              proyecto.
            </h2>
            <p className="font-body text-white/70 leading-relaxed mb-10 max-w-md">
              Cuéntanos sobre tu obra. Nuestro equipo de ingeniería responderá con una propuesta
              técnica clara y un primer estimado preliminar.
            </p>

            <div className="space-y-px bg-white/10">
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="contact-info-phone"
                className="flex items-center gap-5 bg-luxe-black p-5 group hover:bg-[#0d0d0d] transition-colors"
              >
                <div className="w-12 h-12 border border-[#385723] flex items-center justify-center flex-shrink-0 group-hover:bg-[#385723] transition-colors">
                  <Phone className="w-5 h-5 text-[#385723] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-1">
                    Teléfono
                  </div>
                  <div className="font-body text-white">{SITE.phone}</div>
                </div>
              </a>

              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-info-email"
                className="flex items-center gap-5 bg-luxe-black p-5 group hover:bg-[#0d0d0d] transition-colors"
              >
                <div className="w-12 h-12 border border-[#006961] flex items-center justify-center flex-shrink-0 group-hover:bg-[#006961] transition-colors">
                  <Mail className="w-5 h-5 text-[#006961] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-1">
                    Correo
                  </div>
                  <div className="font-body text-white text-sm break-all">{SITE.email}</div>
                </div>
              </a>

              <div className="flex items-start gap-5 bg-luxe-black p-5">
                <div className="w-12 h-12 border border-[#385723] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#385723]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-1">
                    Oficina
                  </div>
                  <div className="font-body text-white text-sm leading-relaxed">
                    {SITE.address}
                    <br />
                    {SITE.city} · {SITE.cp}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-5 bg-luxe-black p-5">
                <div className="w-12 h-12 border border-[#006961] flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-[#006961]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-1">
                    Web
                  </div>
                  <div className="font-body text-white">{SITE.website}</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Form */}
          <div className="lg:col-span-7">
            <div className="bg-luxe-black border border-[rgba(118,113,113,0.2)] p-8 md:p-12">
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="space-y-7"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <div>
                    <label
                      htmlFor="user_name"
                      className="block font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-3"
                    >
                      Nombre completo *
                    </label>
                    <input
                      id="user_name"
                      name="user_name"
                      type="text"
                      data-testid="contact-name-input"
                      value={form.user_name}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      className="w-full bg-transparent border-b border-[#767171]/40 focus:border-[#385723] text-white px-0 py-3 outline-none transition-colors placeholder:text-[#767171]/60"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="user_email"
                      className="block font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-3"
                    >
                      Correo *
                    </label>
                    <input
                      id="user_email"
                      name="user_email"
                      type="email"
                      data-testid="contact-email-input"
                      value={form.user_email}
                      onChange={handleChange}
                      placeholder="ejemplo@correo.com"
                      className="w-full bg-transparent border-b border-[#767171]/40 focus:border-[#385723] text-white px-0 py-3 outline-none transition-colors placeholder:text-[#767171]/60"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <div>
                    <label
                      htmlFor="user_phone"
                      className="block font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-3"
                    >
                      Teléfono
                    </label>
                    <input
                      id="user_phone"
                      name="user_phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={form.user_phone}
                      onChange={handleChange}
                      placeholder="+52 33 0000 0000"
                      className="w-full bg-transparent border-b border-[#767171]/40 focus:border-[#385723] text-white px-0 py-3 outline-none transition-colors placeholder:text-[#767171]/60"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="user_subject"
                      className="block font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-3"
                    >
                      Tipo de proyecto
                    </label>
                    <select
                      id="user_subject"
                      name="user_subject"
                      data-testid="contact-subject-select"
                      value={form.user_subject}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-[#767171]/40 focus:border-[#385723] text-white px-0 py-3 outline-none transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-luxe-black">Selecciona…</option>
                      <option value="Vivienda" className="bg-luxe-black">Vivienda de Lujo</option>
                      <option value="Comercio" className="bg-luxe-black">Comercio</option>
                      <option value="Industrial" className="bg-luxe-black">Industrial</option>
                      <option value="Urbanización" className="bg-luxe-black">Urbanización</option>
                      <option value="Acabados" className="bg-luxe-black">Acabados / Equipamiento</option>
                      <option value="Otro" className="bg-luxe-black">Otro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block font-heading text-xs uppercase tracking-[0.25em] text-[#767171] mb-3"
                  >
                    Cuéntanos sobre tu proyecto *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Ubicación, alcance, metros cuadrados, plazos esperados…"
                    className="w-full bg-transparent border-b border-[#767171]/40 focus:border-[#385723] text-white px-0 py-3 outline-none transition-colors placeholder:text-[#767171]/60 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="btn-luxe w-full sm:w-auto bg-[#385723] hover:text-white text-white font-heading font-semibold uppercase tracking-widest px-10 py-5 transition-colors duration-300 border border-[#385723] flex items-center justify-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Enviando…
                    </>
                  ) : sent ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Mensaje enviado
                    </>
                  ) : (
                    <>
                      Enviar Mensaje
                      <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>

                <p className="font-body text-xs text-[#767171] leading-relaxed">
                  Al enviar este formulario, aceptas que <strong className="text-white/80">{SITE.name}</strong> use
                  tus datos exclusivamente para responder a tu solicitud.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
